<script setup>
import { onMounted, computed } from "vue";
import { useStore } from "vuex";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import ZipcodeInput from "components/pod/pod-build-order/shippingComponents/ZipcodeInput.vue";
import LandingPhone from "components/pod/pod-build-order/contactsComponents/LandingPhone.vue";
import PodPhone from "components/pod/pod-build-order/contactsComponents/PodPhone.vue";
import ArtworkUpload from "components/pod/pod-build-order/uploadComponents/ArtworkUpload.vue";

const store = useStore();
const getDeliveryDate = () => store.dispatch("delivery/getEstimatedDelivery");

const landingImage1 = computed(() => remoteImageStaticUrl("gen/pod/pod-landing-1.png"));
const landingImage2 = computed(() => remoteImageStaticUrl("gen/pod/pod-landing-2-logo.png"));
const landingImage3 = computed(() => remoteImageStaticUrl("gen/pod/pod-landing-3.png"));

onMounted(() => {
  getDeliveryDate();
});
</script>

<template>
  <div class="landing-intro">
    <div class="landing-intro__top-section">
      <div class="landing-intro__delivery-date-section">
        <ZipcodeInput placement="landing" />

        <PodPhone class="landing-intro__phone-desktop" />
      </div>
    </div>
    <div class="landing-intro__container">
      <div class="landing-intro__bg-ellipsis landing-intro__bg-ellipsis--1"></div>
      <div class="landing-intro__bg-ellipsis landing-intro__bg-ellipsis--2"></div>
      <div class="landing-intro__bg-ellipsis landing-intro__bg-ellipsis--3"></div>
      <div class="landing-intro__bg-ellipsis landing-intro__bg-ellipsis--4"></div>
      <div class="landing-intro__content">
        <div class="landing-intro__logo-wrap">
          <UseSvg icon="jiffy-made-border" class="landing-intro__logo" type="print" />

          <LandingPhone class="landing-intro__phone" />
        </div>

        <p class="landing-intro__slogan-title">Custom shirts.</p>
        <p class="landing-intro__slogan-description">Made your way.</p>

        <div class="landing-intro__info">
          <div class="landing-intro__info-row">
            <UseSvg icon="truck-slim" type="print" class="landing-intro__info-icon" />
            Get it in a few days - no sneaky rush fees.
          </div>
          <div class="landing-intro__info-row">
            <UseSvg icon="lower-price-fill" type="print" class="landing-intro__info-icon" />
            All-in prices, as low as $6.
          </div>
          <div class="landing-intro__info-row">
            <UseSvg icon="guarantee" type="print" class="landing-intro__info-icon" />
            Love it or send it back.
          </div>
        </div>

        <ArtworkUpload class="landing-intro__artwork-upload" replica />
      </div>
      <div class="landing-intro__images">
        <img
          class="landing-intro__image landing-intro__image--first"
          :src="landingImage1"
          alt="Image1"
        />
        <img
          class="landing-intro__image landing-intro__image--second"
          :src="landingImage2"
          alt="Image2"
        />
        <img
          class="landing-intro__image landing-intro__image--third"
          :src="landingImage3"
          alt="Image3"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/pod/settings";

.landing-intro {
  $this: &;

  background: $pod-landing-gradient;
  color: $white;
  height: 600px;
  background: $pod-landing-gradient;
  position: relative;
  overflow: hidden;

  @include with-tablet {
    height: auto;
  }

  &__container {
    height: 100%;
    max-width: $small-container-width;
    padding: 25px 20px;
    margin: 0 auto;
    position: relative;
    overflow: hidden;

    @include with-tablet-portrait {
      overflow: initial;
      padding-bottom: 80px;
    }

    @include with-desktop {
      max-width: $container-width;
      display: flex;
      justify-content: space-between;
      padding: 112px 20px 170px;
    }
  }

  &__bg-ellipsis {
    width: 239px;
    height: 239px;
    background: linear-gradient(
      155deg,
      rgba(255, 255, 255, 0.09) 14.08%,
      rgba(255, 255, 255, 0) 40.99%
    );
    border: 1px solid rgba($white, 0.27);
    opacity: 0.98;
    mix-blend-mode: overlay;
    position: absolute;
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;

    @include with-tablet {
      width: 576.068px;
      height: 576.068px;
    }

    &--1 {
      transform: rotate(165deg);
      top: -60px;
      left: -30px;

      @include with-tablet {
        top: -250px;
        left: -250px;
      }
    }

    &--2 {
      transform: rotate(165deg);
      top: 30px;
      left: -90px;

      @include with-tablet {
        top: -100px;
        left: -350px;
      }
    }

    &--3 {
      transform: rotate(-30deg);
      bottom: 120px;
      right: -30px;

      @include with-tablet {
        bottom: -250px;
        right: -250px;
      }
    }

    &--4 {
      transform: rotate(-75deg);
      bottom: 180px;
      right: -90px;

      @include with-tablet {
        bottom: -100px;
        right: -350px;
      }
    }
  }

  &__content {
    z-index: 2;
  }

  &__logo-wrap {
    display: flex;
    margin-bottom: 25px;
    justify-content: space-between;
    align-items: center;
  }

  &__logo {
    width: 123px;
    height: 27px;

    @include with-tablet {
      width: 192px;
      height: 42px;
    }
  }

  &__slogan-title,
  &__slogan-description {
    margin: 0;
    font-size: 32px;
    font-weight: 600;
    line-height: 1.25;

    @include with-desktop {
      font-size: 52px;
    }
  }

  &__slogan-description {
    margin: 0 0 5px;
  }

  &__info {
    margin-top: 24px;
  }

  &__info-row {
    display: flex;
    gap: 8px;
    font-size: 16px;
    line-height: 1.25;
    font-weight: 500;
    align-items: center;

    @include with-desktop {
      font-size: 24px;
      gap: 12px;
    }

    & + & {
      margin-top: 8px;

      @include with-desktop {
        margin-top: 16px;
      }
    }
  }

  &__info-icon {
    width: 24px;
    height: 24px;
    color: $pod-date-orange-bg;

    @include with-tablet {
      width: 28px;
      height: 28px;
    }
  }

  &__top-section {
    width: 100%;
    background: $pod-dark-zipcode;
  }

  &__delivery-date-section {
    position: relative;
    padding: 8px 15px;

    @include with-tablet {
      padding: 8px 15px;
      max-width: $container-width;
      display: flex;
      margin: 0 auto;
      justify-content: space-between;
    }
  }

  &__delivery-date-wrapper {
    color: $pod-gray-modern-900;
    background: $pod-date-orange-bg;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px 20px;
    border-radius: 8px;
  }

  &__delivery-date-icon {
    width: 30px;
    height: 24px;
    animation: slideLeft 3s;

    @include with-large-mobile {
      width: 39px;
      height: 33px;
    }
  }

  &__delivery-date {
    display: block;
    font-size: 20px;
    font-weight: 600;
    line-height: 1;
  }

  &__phone {
    @include with-tablet {
      display: none;
    }
  }

  &__phone-desktop {
    display: none;
    background: none;

    @include with-tablet {
      display: flex;
      font-size: 14px;
      color: $pod-gray-modern-300;
    }

    :deep(.pod-phone__tel) {
      color: $white;
      font-size: 16px;
    }

    :deep(.pod-phone__label) {
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }

  &__images {
    display: flex;
    position: relative;
    margin: 18px -20px 0;

    @include with-desktop {
      width: 590px;
    }
  }

  &__image {
    border-radius: 8px;
    border: 6px solid $white;
    width: 160px;
    height: 160px;
    object-fit: cover;
    position: relative;
    background: $pod-landing-gradient;
    flex: 1 0 160px;

    @include with-desktop {
      border-radius: 24px;
    }

    &--first {
      transform: translateX(-8px);

      @include with-desktop {
        width: 220px;
        height: 220px;
        flex-basis: 220px;
        transform: translate(70px, -60px);
      }
    }

    &--second {
      width: 192px;
      height: 192px;
      z-index: 1;
      transform: translate(-40px, 24px);
      flex-basis: 192px;

      @include with-desktop {
        width: 299px;
        height: 299px;
        flex-basis: 299px;
      }
    }

    &--third {
      width: 169px;
      height: 169px;
      transform: translate(-70px, 9px);
      flex-basis: 169px;

      @include with-desktop {
        width: 189px;
        height: 189px;
        flex-basis: 189px;
        transform: translate(-80px, 170px);
      }
    }
  }

  &__artwork-upload {
    display: none;

    @include with-desktop {
      display: block;
      margin-top: 40px;
    }
  }
}
</style>
